/**
 * Lightweight analytics wrapper — Plausible primary, PostHog optional.
 *
 * Plausible is the right default for a TW MVP:
 *  - No cookies, no fingerprinting → PDPA / GDPR friendly out of the box
 *  - Tiny script (~1 KB)
 *  - We get to skip cookie consent banners
 *
 * PostHog is left wireable (gated by env) for future funnel / session
 * replay needs once we want richer product analytics.
 *
 * Domain + script source come from <meta> tags injected at HTML build
 * time so we don't need a build-time env. Empty/missing meta = no-op.
 */

interface PlausibleAPI {
  (eventName: string, options?: { props?: Record<string, string | number | boolean> }): void;
  q?: unknown[];
}

declare global {
  interface Window {
    plausible?: PlausibleAPI;
  }
}

let initialized = false;

export function initAnalytics(): void {
  if (initialized) return;
  initialized = true;

  const domain = document
    .querySelector<HTMLMetaElement>('meta[name="analytics-domain"]')
    ?.content?.trim();
  if (!domain) return;

  const src = document
    .querySelector<HTMLMetaElement>('meta[name="analytics-src"]')
    ?.content?.trim() || 'https://plausible.io/js/script.js';

  const s = document.createElement('script');
  s.defer = true;
  s.setAttribute('data-domain', domain);
  s.src = src;
  document.head.appendChild(s);

  // Plausible is queueing-tolerant — define stub so callers don't crash
  // before the script loads.
  window.plausible =
    window.plausible ||
    ((eventName: string, options?: { props?: Record<string, string | number | boolean> }) => {
      (window.plausible!.q = window.plausible!.q || []).push([eventName, options]);
    });
}

export function track(
  event: string,
  props?: Record<string, string | number | boolean>,
): void {
  try {
    window.plausible?.(event, props ? { props } : undefined);
  } catch {
    // Never break the app over analytics.
  }
}
